import React, { useState } from "react";
import { Popover, PopoverSurface, PopoverTrigger } from "@fluentui/react-components";
import { X, Plus, Calendar as CalendarIcon, Trash2, Edit2, ChevronDown, ChevronRight, Search, Diamond, AlertCircle, CheckCircle } from "lucide-react";
import Input from "../../../components/controls/input/customInput/CustomInput";
import { CalendarIcon as CalIcon } from "../../../modules/default-svg";
import Modal from "../../../components/controls/modal/Modal";
import styles from "./KeyEventsPopover.module.scss";

const KEY_EVENT_TYPES = {
  TARGET_DATE: 0,
  DATE_RANGE: 1,
};

const KEY_EVENT_REF_TYPES = {
  PROGRAM: 1,
  PROJECT: 2,
};

const KEY_EVENT_ICON_TYPES = {
  CUSTOM: 0,
  MILESTONE: 1,
  DEADLINE: 2,
  REVIEW: 3,
};

const KEY_EVENT_ICONS = {
  [KEY_EVENT_ICON_TYPES.MILESTONE]: { icon: Diamond, label: "Milestone", color: "#9333EA" },
  [KEY_EVENT_ICON_TYPES.DEADLINE]: { icon: AlertCircle, label: "Deadline", color: "#DC2626" },
  [KEY_EVENT_ICON_TYPES.REVIEW]: { icon: CheckCircle, label: "Review", color: "#059669" },
};

const KeyEventsPopover = ({
  open,
  onOpenChange,
  timelineData = [],
  keyEvents = [],
  onCreateEvent,
  onUpdateEvent,
  onDeleteEvent,
  isLoading,
  portfolioId,
  children,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [deleteEventId, setDeleteEventId] = useState(null);
  const [expandedPrograms, setExpandedPrograms] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedGroups, setExpandedGroups] = useState({});

  const [formData, setFormData] = useState({
    title: "",
    type: KEY_EVENT_TYPES.TARGET_DATE,
    refId: "",
    refType: KEY_EVENT_REF_TYPES.PROGRAM,
    startDate: null,
    endDate: null,
    customFields: [],
    iconType: KEY_EVENT_ICON_TYPES.MILESTONE,
  });
  const [errors, setErrors] = useState({});

  const handleBackToList = () => {
    setShowForm(false);
    setEditingEvent(null);
    setFormData({
      title: "",
      type: KEY_EVENT_TYPES.TARGET_DATE,
      refId: "",
      refType: KEY_EVENT_REF_TYPES.PROGRAM,
      startDate: null,
      endDate: null,
      customFields: [],
      iconType: KEY_EVENT_ICON_TYPES.MILESTONE,
    });
    setErrors({});
    setShowAdvanced(false);
    setExpandedPrograms({});
  };

  const handleCreateClick = () => {
    setEditingEvent(null);
    setFormData({
      title: "",
      type: KEY_EVENT_TYPES.TARGET_DATE,
      refId: "",
      refType: KEY_EVENT_REF_TYPES.PROGRAM,
      startDate: null,
      endDate: null,
      customFields: [],
      iconType: KEY_EVENT_ICON_TYPES.MILESTONE,
    });
    setShowForm(true);
  };

  const handleQuickAdd = (refId, refType) => {
    setEditingEvent(null);
    setFormData({
      title: "",
      type: KEY_EVENT_TYPES.TARGET_DATE,
      refId: refId,
      refType: refType,
      startDate: null,
      endDate: null,
      customFields: [],
      iconType: KEY_EVENT_ICON_TYPES.MILESTONE,
    });

    // If adding to a project, expand its parent program
    if (refType === KEY_EVENT_REF_TYPES.PROJECT) {
      for (const program of timelineData) {
        if (program.projects?.some(p => p.id === refId)) {
          setExpandedPrograms(prev => ({
            ...prev,
            [program.id]: true
          }));
          break;
        }
      }
    }

    setShowForm(true);
  };

  const handleEditClick = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      type: event.type,
      refId: event.refId,
      refType: event.refType,
      startDate: event.startDate,
      endDate: event.endDate,
      customFields: event.customFields || [],
      iconType: event.iconType ?? KEY_EVENT_ICON_TYPES.MILESTONE,
    });
    if (event.customFields && event.customFields.length > 0) {
      setShowAdvanced(true);
    }
    setShowForm(true);
  };

  const handleProgramClick = (programId) => {
    setExpandedPrograms((prev) => ({
      ...prev,
      [programId]: !prev[programId],
    }));
  };

  const handleAddCustomField = () => {
    setFormData((prev) => ({
      ...prev,
      customFields: [...prev.customFields, { name: "", value: "" }],
    }));
  };

  const handleRemoveCustomField = (index) => {
    setFormData((prev) => ({
      ...prev,
      customFields: prev.customFields.filter((_, i) => i !== index),
    }));
  };

  const handleCustomFieldChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      customFields: prev.customFields.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Title is required";
    if (!formData.endDate) newErrors.endDate = "Date is required";
    if (formData.type === KEY_EVENT_TYPES.DATE_RANGE && !formData.startDate) {
      newErrors.startDate = "Start date is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const payload = {
      title: formData.title,
      iconType: formData.iconType,
      type: formData.type,
      startDate: formData.type === KEY_EVENT_TYPES.DATE_RANGE ? formData.startDate : null,
      endDate: formData.endDate,
      customFields: formData.customFields.filter(
        (field) => field.name.trim() && field.value.trim()
      ),
    };

    // Only include refId and refType when creating
    if (!editingEvent) {
      payload.refId = formData.refId || null;
      payload.refType = formData.refId ? formData.refType : null;
    }

    if (editingEvent) {
      await onUpdateEvent(editingEvent.id, payload);
    } else {
      await onCreateEvent(payload);
    }
    handleBackToList();
  };

  const handleDeleteClick = (eventId) => {
    setDeleteEventId(eventId);
  };

  const handleConfirmDelete = async () => {
    await onDeleteEvent(deleteEventId);
    setDeleteEventId(null);
  };

  const getRefName = (refId, refType) => {
    for (const program of timelineData) {
      if (refType === KEY_EVENT_REF_TYPES.PROGRAM && program.id === refId) {
        return program.name;
      }
      if (program.projects) {
        for (const project of program.projects) {
          if (refType === KEY_EVENT_REF_TYPES.PROJECT && project.id === refId) {
            return project.name;
          }
        }
      }
    }
    return "";
  };

  // Group events by program and project - show all programs/projects + portfolio level
  const groupEventsByRef = (events) => {
    const grouped = {
      portfolio: {
        type: 'portfolio',
        name: 'Portfolio',
        id: 'portfolio',
        events: [],
      }
    };

    // Initialize all programs and projects
    timelineData.forEach(program => {
      grouped[program.id] = {
        type: 'program',
        name: program.name,
        id: program.id,
        events: [],
        projects: {}
      };

      if (program.projects) {
        program.projects.forEach(project => {
          grouped[program.id].projects[project.id] = {
            type: 'project',
            name: project.name,
            id: project.id,
            events: []
          };
        });
      }
    });

    // Assign events to their groups
    events.forEach(event => {
      // Check if it's a portfolio-level event (no refId or refType)
      if (!event.refId || !event.refType) {
        grouped.portfolio.events.push(event);
        return;
      }

      const program = timelineData.find(p => p.id === event.refId && event.refType === KEY_EVENT_REF_TYPES.PROGRAM);

      if (program && grouped[program.id]) {
        grouped[program.id].events.push(event);
      } else {
        // Find the project and its parent program
        for (const prog of timelineData) {
          const project = prog.projects?.find(p => p.id === event.refId && event.refType === KEY_EVENT_REF_TYPES.PROJECT);
          if (project && grouped[prog.id]?.projects[project.id]) {
            grouped[prog.id].projects[project.id].events.push(event);
            break;
          }
        }
      }
    });

    return grouped;
  };

  // Filter events based on search query
  const filteredEvents = keyEvents.filter(event =>
    event.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupedEvents = groupEventsByRef(filteredEvents);

  // Initialize all groups as expanded including portfolio
  React.useEffect(() => {
    if (Object.keys(groupedEvents).length > 0 && Object.keys(expandedGroups).length === 0) {
      const initialExpanded = {
        'portfolio-portfolio': true // Portfolio group always starts expanded
      };

      Object.keys(groupedEvents).forEach(key => {
        if (key !== 'portfolio') {
          const programId = key;
          initialExpanded[`program-${programId}`] = true;
          Object.keys(groupedEvents[programId].projects || {}).forEach(projectId => {
            initialExpanded[`project-${projectId}`] = true;
          });
        }
      });
      setExpandedGroups(initialExpanded);
    }
  }, [groupedEvents]);

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(e, data) => {
          onOpenChange(data.open);
          if (!data.open) {
            handleBackToList();
            setSearchQuery("");
          }
        }}
        positioning="below-start"
        trapFocus
      >
        <PopoverTrigger disableButtonEnhancement>
          {children}
        </PopoverTrigger>

        <PopoverSurface className={styles.popoverSurface}>
          <div className={styles.popoverContent}>
            {/* Header */}
            <div className={styles.header}>
              <h3 className={styles.title}>
                {showForm ? (editingEvent ? "Edit Event" : "New Event") : "Key Events"}
              </h3>
              <button
                onClick={showForm ? handleBackToList : () => onOpenChange(false)}
                className={styles.closeButton}
              >
                <X size={16} />
              </button>
            </div>

            {/* Content */}
            <div className={styles.content}>
              {showForm ? (
                <div className={styles.formContainer}>
                  {/* Title */}
                  <Input
                    label="Title"
                    placeholder="Enter event title"
                    value={formData.title}
                    handleChange={(value) => setFormData(prev => ({ ...prev, title: value }))}
                    error={errors.title}
                    isRequired
                  />

                  {/* Program/Project Selection */}
                  {!editingEvent && (
                    <div>
                      <label className="Input_label text-sm">
                        Attach to <span className="text-red-500">*</span>
                      </label>
                      {errors.refId && (
                        <p className="text-xs text-red-500 mt-1">{errors.refId}</p>
                      )}
                      <div className={styles.programProjectList}>
                        {/* Portfolio Level Option */}
                        <div className={styles.programItem}>
                          <div
                            className={`${styles.programRow} ${!formData.refId && !formData.refType
                                ? styles.selected
                                : ""
                              }`}
                            onClick={() => setFormData(prev => ({ ...prev, refId: null, refType: null }))}
                          >
                            <span className={styles.programName}>Portfolio (Global)</span>
                            {!formData.refId && !formData.refType && (
                              <div className={styles.selectionIndicator} />
                            )}
                          </div>
                        </div>

                        {/* Programs and Projects */}
                        {timelineData.map((program) => (
                          <div key={program.id} className={styles.programItem}>
                            <div
                              className={`${styles.programRow} ${formData.refId === program.id && formData.refType === KEY_EVENT_REF_TYPES.PROGRAM
                                  ? styles.selected
                                  : ""
                                }`}
                              onClick={() => setFormData(prev => ({ ...prev, refId: program.id, refType: KEY_EVENT_REF_TYPES.PROGRAM }))}
                            >
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleProgramClick(program.id);
                                }}
                                className={styles.expandButton}
                              >
                                {expandedPrograms[program.id] ? (
                                  <ChevronDown size={14} />
                                ) : (
                                  <ChevronRight size={14} />
                                )}
                              </button>
                              <span className={styles.programName}>{program.name}</span>
                              {formData.refId === program.id && formData.refType === KEY_EVENT_REF_TYPES.PROGRAM && (
                                <div className={styles.selectionIndicator} />
                              )}
                            </div>

                            {expandedPrograms[program.id] && program.projects && (
                              <div className={styles.projectsList}>
                                {program.projects.map((project) => (
                                  <div
                                    key={project.id}
                                    className={`${styles.projectRow} ${formData.refId === project.id && formData.refType === KEY_EVENT_REF_TYPES.PROJECT
                                        ? styles.selected
                                        : ""
                                      }`}
                                    onClick={() => setFormData(prev => ({ ...prev, refId: project.id, refType: KEY_EVENT_REF_TYPES.PROJECT }))}
                                  >
                                    <span className={styles.projectName}>{project.name}</span>
                                    {formData.refId === project.id && formData.refType === KEY_EVENT_REF_TYPES.PROJECT && (
                                      <div className={styles.selectionIndicator} />
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Icon Type */}
                  <div>
                    <label className="Input_label text-sm">
                      Icon Type
                    </label>
                    <div className={styles.iconTypeButtons}>
                      {Object.entries(KEY_EVENT_ICONS).map(([type, { icon: Icon, label, color }]) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, iconType: parseInt(type) }))}
                          className={`${styles.iconTypeButton} ${formData.iconType === parseInt(type) ? styles.active : ""}`}
                        >
                          <Icon size={16} style={{ color: formData.iconType === parseInt(type) ? color : '#6b7280' }} />
                          <span>{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {formData.refId && formData.refType && (
                    <>
                      {/* Event Type */}
                      <div>
                        <label className="Input_label text-sm">
                          Event Type
                        </label>
                        <div className={styles.eventTypeButtons}>
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, type: KEY_EVENT_TYPES.TARGET_DATE }))}
                            className={`${styles.eventTypeButton} ${formData.type === KEY_EVENT_TYPES.TARGET_DATE ? styles.active : ""}`}
                          >
                            Target Date
                          </button>
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, type: KEY_EVENT_TYPES.DATE_RANGE }))}
                            className={`${styles.eventTypeButton} ${formData.type === KEY_EVENT_TYPES.DATE_RANGE ? styles.active : ""}`}
                          >
                            Date Range
                          </button>
                        </div>
                      </div>
                    </>
                  )}


                  {/* Dates */}
                  <div className={styles.datesContainer}>
                    {formData.type === KEY_EVENT_TYPES.DATE_RANGE && (
                      <div className={styles.dateField}>
                        <Input
                          label="Start Date"
                          icon={<CalIcon />}
                          type="date"
                          value={formData.startDate}
                          maxDate={formData.endDate}
                          handleChange={(value) => setFormData(prev => ({ ...prev, startDate: value }))}
                          error={errors.startDate}
                          isRequired
                        />
                      </div>
                    )}
                    <div className={styles.dateField}>
                      <Input
                        label={formData.type === KEY_EVENT_TYPES.TARGET_DATE ? "Target Date" : "End Date"}
                        icon={<CalIcon />}
                        type="date"
                        value={formData.endDate}
                        minDate={formData.startDate}
                        handleChange={(value) => setFormData(prev => ({ ...prev, endDate: value }))}
                        error={errors.endDate}
                        isRequired
                      />
                    </div>
                  </div>

                  {/* Custom Fields Section */}
                  <div className={styles.customFieldsSection}>
                    <div className={styles.customFieldsHeader}>
                      <button
                        type="button"
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        className={styles.customFieldsToggle}
                      >
                        {showAdvanced ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        <span>Custom Fields</span>
                      </button>
                      {showAdvanced && (
                        <button
                          type="button"
                          onClick={handleAddCustomField}
                          className={styles.addFieldButton}
                          title="Add Field"
                        >
                          <Plus size={14} className="text-[#342C82]" />
                        </button>
                      )}
                    </div>

                    {showAdvanced && (
                      <div className={styles.customFieldsList}>
                        {formData.customFields.map((field, index) => (
                          <div key={index} className={styles.customFieldRow}>
                            <Input
                              placeholder="Name"
                              value={field.name}
                              handleChange={(value) => handleCustomFieldChange(index, "name", value)}
                            />
                            <Input
                              placeholder="Value"
                              value={field.value}
                              handleChange={(value) => handleCustomFieldChange(index, "value", value)}
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveCustomField(index)}
                              className={styles.removeFieldButton}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                        {formData.customFields.length === 0 && (
                          <p className={styles.emptyFieldsMessage}>No custom fields added</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className={styles.formActions}>
                    <button
                      type="button"
                      onClick={handleBackToList}
                      disabled={isLoading}
                      className={styles.cancelButton}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={isLoading}
                      className={styles.saveButton}
                    >
                      {isLoading ? "Saving..." : editingEvent ? "Update" : "Create"}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Search Bar */}
                  {keyEvents.length > 0 && (
                    <div className={styles.searchContainer}>
                      <div className={styles.searchWrapper}>
                        <div className={styles.searchInputWrapper}>
                          <Search size={14} className={styles.searchIcon} />
                          <input
                            type="text"
                            placeholder="Search events..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className={styles.searchInput}
                          />
                        </div>
                        <button onClick={handleCreateClick} className={styles.addButton}>
                          <Plus size={14} />
                          <span>Add</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Events List */}
                  {keyEvents.length === 0 ? (
                    <div className={styles.emptyState}>
                      <CalendarIcon size={48} className={styles.emptyIcon} />
                      <p className={styles.emptyTitle}>No key events yet</p>
                      <p className={styles.emptySubtitle}>Click "Add" to create your first key event</p>
                      <button onClick={handleCreateClick} className={styles.addButton}>
                        <Plus size={14} />
                        <span>Add</span>
                      </button>
                    </div>
                  ) : filteredEvents.length === 0 ? (
                    <div className={styles.emptyState}>
                      <Search size={48} className={styles.emptyIcon} />
                      <p className={styles.emptyTitle}>No events found</p>
                      <p className={styles.emptySubtitle}>Try a different search term</p>
                    </div>
                  ) : (
                    <div className={styles.eventsList}>
                      {/* Portfolio Level Events */}
                      {groupedEvents.portfolio && (
                        <div className={styles.groupContainer}>
                          <div className={styles.programGroup}>
                            <div className={styles.groupHeader}>
                              <div className={styles.groupHeaderLeft}>
                                <div onClick={() => toggleGroup('portfolio-portfolio')} style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                  <button className={styles.expandIcon}>
                                    {expandedGroups['portfolio-portfolio'] ? (
                                      <ChevronDown size={16} />
                                    ) : (
                                      <ChevronRight size={16} />
                                    )}
                                  </button>
                                  <span className={styles.groupTitle}>Portfolio (Global)</span>
                                </div>
                                <button
                                  className={styles.quickAddButton}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFormData(prev => ({ ...prev, refId: null, refType: null }));
                                    setShowForm(true);
                                  }}
                                  title="Add event"
                                >
                                  <Plus size={14} />
                                </button>
                              </div>
                              <div className={styles.groupHeaderRight}>
                                <span className={styles.eventCount}>{groupedEvents.portfolio.events.length}</span>
                              </div>
                            </div>
                            {expandedGroups['portfolio-portfolio'] && (
                              <>
                                {groupedEvents.portfolio.events.length > 0 ? (
                                  <div className={styles.groupEvents}>
                                    {groupedEvents.portfolio.events.map((event) => (
                                      <div
                                        key={event.id}
                                        className={styles.eventCard}
                                        onClick={() => handleEditClick(event)}
                                      >
                                        <div className={styles.eventContent}>
                                          <div className={styles.eventHeader}>
                                            <div className={styles.eventInfo}>
                                              <h4 className={styles.eventTitle}>{event.title}</h4>
                                              <p className={styles.eventDate}>
                                                {event.type === KEY_EVENT_TYPES.TARGET_DATE
                                                  ? event.endDate
                                                  : `${event.startDate} - ${event.endDate}`}
                                              </p>
                                            </div>
                                            <div className={styles.eventActions}>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeleteClick(event.id);
                                                }}
                                                className={styles.deleteButton}
                                                title="Delete"
                                              >
                                                <X size={16} />
                                              </button>
                                            </div>
                                          </div>
                                        </div>

                                        {event.customFields && event.customFields.length > 0 && (
                                          <div className={styles.customFieldsDisplay}>
                                            <div className={styles.customFieldsContent}>
                                              {event.customFields.map((field, index) => (
                                                <div key={index} className={styles.customFieldItem}>
                                                  <span className={styles.customFieldName}>{field.name}:</span>
                                                  <span className={styles.customFieldValue}>{field.value}</span>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className={styles.emptyGroupState}>
                                    <p className={styles.emptyGroupText}>No portfolio-level events yet</p>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Programs and Projects Groups */}
                      {Object.entries(groupedEvents).filter(([key]) => key !== 'portfolio').map(([programId, programGroup]) => (
                        <div key={programId} className={styles.groupContainer}>
                          <div className={styles.programGroup}>
                            <div
                              className={styles.groupHeader}
                            >
                              <div
                                className={styles.groupHeaderLeft}
                              >
                                <div onClick={() => toggleGroup(`program-${programGroup.id}`)} style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                  <button className={styles.expandIcon}>
                                    {expandedGroups[`program-${programGroup.id}`] ? (
                                      <ChevronDown size={16} />
                                    ) : (
                                      <ChevronRight size={16} />
                                    )}
                                  </button>
                                  <span className={styles.groupTitle}>{programGroup.name}</span>
                                </div>
                                <button
                                  className={styles.quickAddButton}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleQuickAdd(programGroup.id, KEY_EVENT_REF_TYPES.PROGRAM);
                                  }}
                                  title="Add event"
                                >
                                  <Plus size={14} />
                                </button>
                              </div>
                              <div className={styles.groupHeaderRight}>
                                <span className={styles.eventCount}>{programGroup.events.length}</span>
                              </div>
                            </div>
                            {expandedGroups[`program-${programGroup.id}`] && (
                              <>
                                {programGroup.events.length > 0 ? (
                                  <div className={styles.groupEvents}>
                                    {programGroup.events.map((event) => (
                                      <div
                                        key={event.id}
                                        className={styles.eventCard}
                                        onClick={() => handleEditClick(event)}
                                      >
                                        <div className={styles.eventContent}>
                                          <div className={styles.eventHeader}>
                                            <div className={styles.eventInfo}>
                                              <h4 className={styles.eventTitle}>{event.title}</h4>
                                              <p className={styles.eventDate}>
                                                {event.type === KEY_EVENT_TYPES.TARGET_DATE
                                                  ? event.endDate
                                                  : `${event.startDate} - ${event.endDate}`}
                                              </p>
                                            </div>
                                            <div className={styles.eventActions}>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDeleteClick(event.id);
                                                }}
                                                className={styles.deleteButton}
                                                title="Delete"
                                              >
                                                <X size={16} />
                                              </button>
                                            </div>
                                          </div>
                                        </div>

                                        {event.customFields && event.customFields.length > 0 && (
                                          <div className={styles.customFieldsDisplay}>
                                            <div className={styles.customFieldsContent}>
                                              {event.customFields.map((field, index) => (
                                                <div key={index} className={styles.customFieldItem}>
                                                  <span className={styles.customFieldName}>{field.name}:</span>
                                                  <span className={styles.customFieldValue}>{field.value}</span>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className={styles.emptyGroupState}>
                                    <p className={styles.emptyGroupText}>No events yet</p>
                                  </div>
                                )}
                              </>
                            )}
                          </div>

                          {/* Project Events */}
                          {Object.values(programGroup.projects).map((projectGroup) => (
                            <div key={projectGroup.id} className={styles.projectGroup}>
                              <div
                                className={styles.groupHeader}
                              >
                                <div
                                  className={styles.groupHeaderLeft}
                                >
                                  <div onClick={() => toggleGroup(`project-${projectGroup.id}`)} style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                                    <button className={styles.expandIcon}>
                                      {expandedGroups[`project-${projectGroup.id}`] ? (
                                        <ChevronDown size={16} />
                                      ) : (
                                        <ChevronRight size={16} />
                                      )}
                                    </button>
                                    <span className={styles.groupTitle}>{projectGroup.name}</span>
                                  </div>
                                  <button
                                    className={styles.quickAddButton}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleQuickAdd(projectGroup.id, KEY_EVENT_REF_TYPES.PROJECT);
                                    }}
                                    title="Add event"
                                  >
                                    <Plus size={14} />
                                  </button>
                                </div>
                                <div className={styles.groupHeaderRight}>
                                  <span className={styles.eventCount}>{projectGroup.events.length}</span>
                                </div>
                              </div>
                              {expandedGroups[`project-${projectGroup.id}`] && (
                                <>
                                  {projectGroup.events.length > 0 ? (
                                    <div className={styles.groupEvents}>
                                      {projectGroup.events.map((event) => (
                                        <div
                                          key={event.id}
                                          className={styles.eventCard}
                                          onClick={() => handleEditClick(event)}
                                        >
                                          <div className={styles.eventContent}>
                                            <div className={styles.eventHeader}>
                                              <div className={styles.eventInfo}>
                                                <h4 className={styles.eventTitle}>{event.title}</h4>
                                                <p className={styles.eventDate}>
                                                  {event.type === KEY_EVENT_TYPES.TARGET_DATE
                                                    ? event.endDate
                                                    : `${event.startDate} - ${event.endDate}`}
                                                </p>
                                              </div>
                                              <div className={styles.eventActions}>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteClick(event.id);
                                                  }}
                                                  className={styles.deleteButton}
                                                  title="Delete"
                                                >
                                                  <X size={16} />
                                                </button>
                                              </div>
                                            </div>
                                          </div>

                                          {event.customFields && event.customFields.length > 0 && (
                                            <div className={styles.customFieldsDisplay}>
                                              <div className={styles.customFieldsContent}>
                                                {event.customFields.map((field, index) => (
                                                  <div key={index} className={styles.customFieldItem}>
                                                    <span className={styles.customFieldName}>{field.name}:</span>
                                                    <span className={styles.customFieldValue}>{field.value}</span>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className={styles.emptyGroupState}>
                                      <p className={styles.emptyGroupText}>No events yet</p>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </PopoverSurface>
      </Popover>

      {/* Delete Confirmation Modal */}
      {deleteEventId && (
        <Modal
          title="Delete Key Event"
          body={
            <p className="text-sm-400 subHeading-color" style={{ fontFamily: "Figtree, sans-serif" }}>
              Are you sure you want to delete this key event? This action cannot be undone.
            </p>
          }
          handleCancel={() => setDeleteEventId(null)}
          handleSave={handleConfirmDelete}
          saveBtnLabel="Delete"
          cancelBtnLabel="Cancel"
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default KeyEventsPopover;
